import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter } from 'rxjs';
import { selectCurrentSite, selectSites } from 'src/app/store/site/site.selector';
import { Site } from 'src/app/model/site';
import { UserFile } from 'src/app/model/user-file';
import { selectSiteImages } from 'src/app/store/user-file/user-file.selector';
import { UserFileService } from 'src/app/services/user-file.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { SiteSelectorDialogComponent } from '../site-selector-dialog/site-selector.dialog.component';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { selectIsDraftMode } from 'src/app/store/user/user.selector';
import { StandardPageLayout } from '../../stdlayoutlib/classes.stdlayoutlib';
import * as SiteActions from 'src/app/store/site/site.actions';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-site-selector',
	templateUrl: './site-selector.component.html',
	providers: [DialogService],
	styles:
		`
		#siteCardMobileImage {
			max-width: 82.5px;
			max-height: 54.5px;
		}

		img {
			-webkit-user-drag: none;
			user-drag: none;
		}
		`
})
export class SiteSelectorComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;

	siteImages$: Observable<UserFile[] | undefined>;
	siteImagesSub: Subscription | undefined;

	siteImages: UserFile[];

	site: Site;
	sites: Site[];
	siteImage = { src: 'assets/images/tablet-bg.jpg' };

	isDraftMode: boolean;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription;

	ref: DynamicDialogRef | undefined;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		public dialogService: DialogService,
		private translate: TranslateService,
		private userFileService: UserFileService,
		private route: ActivatedRoute,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.store.dispatch(UserFileActions.getSiteImages());
		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.siteSub?.unsubscribe();
		this.siteImagesSub?.unsubscribe();
		this.sitesSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
	}

	initSelectors(): void {
		// Store selectors
		this.site$ = this.store.select(selectCurrentSite);
		this.siteImages$ = this.store.select(selectSiteImages);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.sites$ = this.store.select(selectSites);
	}

	subscribeAll(): void {
		this.siteSub = this.site$?.subscribe((site) => {
			this.site = site;
			if (site !== undefined) {
				this.getImage();
			} else if (this.sites !== undefined && this.sites.length > 0 && !this.isDraftMode) {
				this.store.dispatch(SiteActions.setCurrentSite({ siteId: this.sites[0].Id }));
			}
		});
		this.siteImagesSub = this.siteImages$.subscribe((userFiles) => {
			this.siteImages = userFiles;
			if (this.site !== undefined) {
				this.getImage();
			}
		});
		this.isDraftModeSub = this.isDraftMode$.subscribe(
			(isDraftMode) => {
				this.isDraftMode = isDraftMode;
			}
		);
		this.sitesSub = this.sites$.pipe(
			filter(sites => sites.length > 0)
		).subscribe((sites) => {
			this.sites = sites;
			const param = this.route.snapshot.queryParams['siteId'];
			if (this.site === undefined && !this.isDraftMode && param === undefined) {
				this.store.dispatch(SiteActions.setCurrentSite({ siteId: sites[0].Id }));
			}
		});
	}

	getImage(): void {
		if (this.siteImages) {
			const userFile = this.siteImages.find(f => f.SiteId === this.site.Id);
			if (!userFile) {
				this.siteImage = { src: 'assets/images/tablet-bg.jpg' };
				//this.siteImagesSub?.unsubscribe(); WHY here??
				return;
			} else {
				this.siteImagesSub = this.userFileService.getUserFileContent(userFile.Id)
					.pipe(filter(resp => !!resp))
					.subscribe(resp => {
						this.siteImage = { src: resp };
					});
			}

		}
	}

	getSelectableLayoutCard(): string {
		return (!this.isDraftMode) ? 'hover:surface-500 cursor-pointer' : '';
	}

	changeSite(): void {
		if (this.site !== undefined && !this.isDraftMode) {
			this.ref = this.dialogService.open(SiteSelectorDialogComponent, {
				header: this.translate.instant('SELECT_SITE'),
				width: '95vw',
				contentStyle: { overflow: 'auto' },
				data: {
					siteName: this.site.Name
				}
			});
		}
	}

	imageSource(): { src: string } {
		return this.siteImage;
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
